<template>
  <CollapsibleSection
    v-if="getValue('formation_lieu') === 'INTRA' || getValue('formation_lieu') === 'LOCATION'"
    title="Lieu de la formation"
    icon="fas fa-map-marker-alt"
    context="quote"
    storage-key="lieu-formation-info"
    :bare="bare"
  >
    <DetailItem label="Adresse">
      <Input
        v-if="isEditing"
        ref="addressAutocompleteRef"
        :model-value="editedQuote.lieu_formation_adresse"
        @update:model-value="editedQuote.lieu_formation_adresse = $event"
        placeholder="Rechercher une adresse..."
      />
      <template v-else>
        <span v-if="!isMissing(quote.lieu_formation_adresse)">{{ quote.lieu_formation_adresse }}</span>
        <DetailEmpty v-else />
      </template>
    </DetailItem>
    <DetailItem label="Code postal">
      <input
        v-if="isEditing"
        v-model="editedQuote.lieu_formation_code_postal"
        class="form-control"
        type="text"
        placeholder="Code postal"
      />
      <template v-else>
        <span v-if="!isMissing(quote.lieu_formation_code_postal)">{{ quote.lieu_formation_code_postal }}</span>
        <DetailEmpty v-else />
      </template>
    </DetailItem>
    <DetailItem label="Ville">
      <input
        v-if="isEditing"
        v-model="editedQuote.lieu_formation_ville"
        class="form-control"
        type="text"
        placeholder="Ville"
      />
      <template v-else>
        <span v-if="!isMissing(quote.lieu_formation_ville)">{{ quote.lieu_formation_ville }}</span>
        <DetailEmpty v-else />
      </template>
    </DetailItem>
    <!-- Bac à flamme et camion : réponses des demandes reçues AVANT que ces
         questions passent au questionnaire de qualification. Elles restent
         lisibles là où elles existent ; une demande récente porte la réponse
         dans l'analyse du besoin. -->
    <DetailItem
      v-if="quote.emplacement_exterieur_bac_flamme"
      label="Emplacement extérieur pour le bac à flamme"
    >
      <RadioGroup
        v-if="isEditing"
        v-model="editedQuote.emplacement_exterieur_bac_flamme"
        :options="bacFlammeOptions"
        vertical
      />
      <template v-else>
        <span v-if="libelleBacFlamme">{{ libelleBacFlamme }}</span>
        <DetailEmpty v-else />
      </template>
    </DetailItem>
    <DetailItem
      v-if="quote.emplacement_camion_unite_mobile"
      label="Emplacement du camion en unité mobile"
    >
      <RadioGroup
        v-if="isEditing"
        v-model="editedQuote.emplacement_camion_unite_mobile"
        :options="camionOptions"
        vertical
      />
      <template v-else>
        <span v-if="libelleCamion">{{ libelleCamion }}</span>
        <DetailEmpty v-else />
      </template>
    </DetailItem>
    <DetailItem
      v-if="getValue('formation_lieu') === 'INTRA'"
      label="Stationnement pour le formateur"
    >
      <RadioGroup
        v-if="isEditing"
        v-model="editedQuote.stationnement_formateur"
        :options="stationnementOptions"
        vertical
      />
      <template v-else>
        <span v-if="libelleStationnement">{{ libelleStationnement }}</span>
        <DetailEmpty v-else />
      </template>
    </DetailItem>
    <!-- Le nombre de participants se demande dans le bloc Formation, avec la
         taille et le nombre de groupes : les trois décident du prix, et le
         poser ici le mettait après le stationnement du formateur. -->
    <!-- Le commentaire du client se lit et se complète dans le bloc
         « La demande », avec la formation qu'il décrit. -->

    <!-- Les horaires de la formation se demandent dans « Disponibilités du
         client » : ils disent quand on intervient, pas où — et cette section
         n'existe pas pour une formation INTER. -->

    <!-- Carte Google Maps -->
    <div class="detail-item-full map-section">
      <GoogleMap
        :address="mapAddress"
        :postal-code="mapPostalCode"
        :city="mapCity"
      />
    </div>
    <template #footer>
      <slot name="footer" />
    </template>
  </CollapsibleSection>
</template>

<script setup>
import { computed, ref, watch, nextTick } from 'vue'
import { CollapsibleSection, DetailItem, DetailEmpty } from '../data'
import { GoogleMap, RadioGroup, Input } from '../ui'
import { useGooglePlaces } from '../../composables/useGooglePlaces.js'

import {
  BAC_FLAMME_OPTIONS as bacFlammeOptions,
  CAMION_UNITE_MOBILE_OPTIONS as camionOptions,
  STATIONNEMENT_FORMATEUR_OPTIONS as stationnementOptions,
  libelleOption,
} from '@shared/quote-intra-location.js'

const props = defineProps({
  /**
   * Mode « nu » : le contenu seul, sans carte ni en-tête repliable. La fenêtre
   * « Analyse du besoin » v2 n'affiche qu'une section à la fois et porte
   * elle-même son titre — la carte y ferait un cadre dans un cadre.
   * Additif : sans la prop, le rendu est exactement celui d'avant.
   */
  bare: {
    type: Boolean,
    default: false
  },
  quote: {
    type: Object,
    required: true
  },
  isEditing: {
    type: Boolean,
    default: false
  },
  editedQuote: {
    type: Object,
    default: () => ({})
  },
  getValue: {
    type: Function,
    required: true
  }
})

/**
 * LE LIBELLÉ SE LIT DANS LA LISTE, il ne se réécrit pas en face de chaque
 * valeur. Une cascade de `v-if` oblige à revenir ici à chaque réponse
 * ajoutée ailleurs — c'est ce qui a fait afficher « Non renseigné » sur une
 * demande répondue « Je ne sais pas ». Une valeur hors liste n'a pas de
 * libellé, et c'est bien « Non renseigné » qu'il faut alors afficher.
 */
const libelleBacFlamme = computed(() => libelleOption(bacFlammeOptions, props.quote.emplacement_exterieur_bac_flamme))
const libelleCamion = computed(() => libelleOption(camionOptions, props.quote.emplacement_camion_unite_mobile))
const libelleStationnement = computed(() => libelleOption(stationnementOptions, props.quote.stationnement_formateur))

const isMissing = (value) => {
  return !value || value === '' || value === null || value === undefined
}

// Google Places autocomplete for address
const addressAutocompleteRef = ref(null)
const { initAutocomplete } = useGooglePlaces({
  onPlaceSelected: (addressData) => {
    props.editedQuote.lieu_formation_adresse = addressData.streetAddress || addressData.formattedAddress
    props.editedQuote.lieu_formation_code_postal = addressData.postalCode
    props.editedQuote.lieu_formation_ville = addressData.locality
  },
  types: ['address'],
  componentRestrictions: { country: 'fr' }
})

// Init autocomplete when entering edit mode
watch(() => props.isEditing, async (editing) => {
  if (editing) {
    await nextTick()
    await nextTick()
    const inputEl = addressAutocompleteRef.value?.inputRef
    if (inputEl) {
      initAutocomplete(inputEl)
    }
  }
}, { immediate: true })

const mapAddress = computed(() => {
  return props.isEditing
    ? props.editedQuote.lieu_formation_adresse
    : props.quote.lieu_formation_adresse
})

const mapPostalCode = computed(() => {
  return props.isEditing
    ? props.editedQuote.lieu_formation_code_postal
    : props.quote.lieu_formation_code_postal
})

const mapCity = computed(() => {
  return props.isEditing
    ? props.editedQuote.lieu_formation_ville
    : props.quote.lieu_formation_ville
})
</script>

<style scoped>
.detail-item-full {
  grid-column: 1 / -1;
}

.map-section {
  margin-top: var(--spacing-md);
  padding-top: var(--spacing-md);
  border-top: 1px solid var(--color-border-light);
}
</style>
